import { useMemo } from 'react';

import { useCatalogProductIdParams, useIsAuthenticated } from '@domains/pdp-common';

import { useSizeComparison } from '../../hooks/useSizeComparison';
import { useSizeInfoTab } from '../../hooks/useSizeInfoTab';
import { useUserMeasureRow } from '../../hooks/useUserMeasureRow';
import { useMyPurchaseSizeList } from '../../queries/useMyPurchaseSizeList';
import { useSizeInfo } from '../../queries/useSizeInfoQuery';
import { transformSizeData } from './data-transform';
import { SizeInfoItem } from './types';

/**
 * 개별 사이즈 아이템에 대한 데이터 및 상태를 관리하는 훅
 * 각 size_category_id별로 독립적인 상태를 유지합니다.
 */
export const useActualSizeItemData = (rawItem: SizeInfoItem | undefined) => {
  const param = useCatalogProductIdParams();
  const { isLoggedIn, isLoading } = useIsAuthenticated();

  // 유저 실측 매핑 로직
  const {
    userMeasureRow,
    refetchUserMeasurement,
    isUserSizeAgreed,
    isLoading: isUserSizeLoading,
  } = useUserMeasureRow({
    sizeCategoryId: rawItem?.sizeCategoryId,
    headers: rawItem?.headers,
  });

  // 구매내역 데이터 조회
  const { data: purchaseDataForTab, isLoading: isPurchaseLoading } = useMyPurchaseSizeList(rawItem?.sizeCategoryId);

  const isSizeComparable = rawItem?.isSizeComparable ?? false;
  const { activeTab, onTabClick } = useSizeInfoTab(isSizeComparable, isLoggedIn, {
    hasPurchaseData: isPurchaseLoading ? undefined : purchaseDataForTab.length > 0,
    hasUserSizeData: isUserSizeLoading ? undefined : !!userMeasureRow,
  });

  // 비교 상품(구매 내역) 데이터 조회
  const {
    myPurchaseData,
    selectedPurchaseId,
    onPurchaseCardClick,
    compareRow: purchaseCompareRow,
    selectedPurchaseImage,
  } = useSizeComparison(rawItem, activeTab);

  return {
    rawItem,
    userMeasureRow,
    refetchUserMeasurement,
    isUserSizeAgreed,
    isLoggedIn,
    isLoading,
    isSizeComparable,
    activeTab,
    onTabClick,
    myPurchaseData,
    selectedPurchaseId,
    onPurchaseCardClick,
    purchaseCompareRow,
    selectedPurchaseImage,
    catalogProductId: param.catalog_product_id,
  };
};

/**
 * 전체 사이즈 아이템 목록을 반환하는 훅
 * 여러 size_category_id를 가진 상품을 위해 배열로 반환합니다.
 */
export const useActualSizeItemList = () => {
  const param = useCatalogProductIdParams();
  const { isLoading } = useIsAuthenticated();

  const { data: sizeInfoData } = useSizeInfo(param);

  const rawItems = useMemo(() => transformSizeData(sizeInfoData?.item_list || []), [sizeInfoData?.item_list]);

  return {
    rawItems,
    isLoading,
    catalogProductId: param.catalog_product_id,
  };
};

/**
 * @deprecated 단일 아이템 기반 훅. 다중 사이즈 카테고리 지원을 위해 useActualSizeItemList + useActualSizeItemData 사용 권장
 */
export const useActualSizeData = () => {
  const param = useCatalogProductIdParams();
  const { isLoggedIn, isLoading } = useIsAuthenticated();

  // 1. 상품 사이즈 정보 조회
  const { data: sizeInfoData } = useSizeInfo(param);

  const rawItem = useMemo(() => transformSizeData(sizeInfoData?.item_list || [])[0], [sizeInfoData?.item_list]);

  // 2. 유저 실측 매핑 로직
  const {
    userMeasureRow,
    refetchUserMeasurement,
    isUserSizeAgreed,
    isLoading: isUserSizeLoading,
  } = useUserMeasureRow({
    sizeCategoryId: rawItem?.sizeCategoryId,
    headers: rawItem?.headers,
  });

  // 3. 구매내역 데이터 조회 (탭 결정을 위해 먼저 조회)
  // react-query 캐싱으로 useSizeComparison 내부 호출과 중복되지 않음
  const { data: purchaseDataForTab, isLoading: isPurchaseLoading } = useMyPurchaseSizeList(rawItem?.sizeCategoryId);

  const isSizeComparable = rawItem?.isSizeComparable ?? false;
  const { activeTab, onTabClick } = useSizeInfoTab(isSizeComparable, isLoggedIn, {
    // 로딩 중일 때는 undefined로 전달하여 탭 초기화를 지연시킴
    hasPurchaseData: isPurchaseLoading ? undefined : purchaseDataForTab.length > 0,
    hasUserSizeData: isUserSizeLoading ? undefined : !!userMeasureRow,
  });

  // 4. 비교 상품(구매 내역) 데이터 조회
  const {
    myPurchaseData,
    selectedPurchaseId,
    onPurchaseCardClick,
    compareRow: purchaseCompareRow,
    selectedPurchaseImage,
  } = useSizeComparison(rawItem, activeTab);

  return {
    // Raw Data & State
    rawItem,
    userMeasureRow,
    refetchUserMeasurement,
    isUserSizeAgreed,
    isLoggedIn,
    isLoading,
    isSizeComparable,
    activeTab,
    onTabClick,

    // Purchase Data
    myPurchaseData,
    selectedPurchaseId,
    onPurchaseCardClick,
    purchaseCompareRow,
    selectedPurchaseImage,

    // Utils for upper layer
    catalogProductId: param.catalog_product_id,
  };
};
